/**
 * What a capture URL becomes once it is safe to hand to an `<img>` under this portal's CSP.
 *
 * `src` is a `blob:` object URL on success, and simply the original URL, unchanged, whenever there
 * was nothing to convert or the conversion could not be trusted. That second case matters: it means
 * a caller that hands the result straight to a component built before this fix (`src?: string`)
 * gets exactly the same failure behaviour it had before, with no new branch to keep in sync.
 */
export type TDisplayableCapture = {
  src?: string;
  /**
   * Releases the object URL this call created, if any. Safe to call when nothing was created (the
   * input was absent, or the fetch fell back to the original URL): `URL.revokeObjectURL` is never
   * invoked on a URL this function did not itself mint.
   */
  release: () => void;
};

const NOTHING_CREATED: TDisplayableCapture['release'] = () => {};

/**
 * Fetches `url` and converts it to a `blob:` object URL, the technique `PortraitDisplay` already
 * proved works against this policy: `img-src` allows `blob:` but not a presigned S3 host, and
 * `connect-src` is absent from the policy, so the fetch itself is unrestricted.
 *
 * **Falls back to the original URL, not to `undefined`, whenever the conversion cannot be trusted**
 * (no URL was supplied, the response was not ok, or the fetch itself rejected). Handing back
 * `undefined` on a genuine failure would read as "no capture", which is a different fact from "a
 * capture exists but its URL no longer resolves": the two already have different UI treatments
 * (`EvidenceThumbnail`'s "No capture yet" versus its failed-image icon; `CaptureViewer`'s "Not yet
 * available" versus "Capture failed to load"). Handing back the original URL instead keeps a
 * genuinely broken capture on the failure path it was already on: the browser still refuses to load
 * it (blocked by the policy, or simply gone), so the component's own `onError` still fires and the
 * failure tile still shows, exactly as it did before this function existed.
 *
 * This is deliberately not a thrown exception on a non-ok response: a 404 on a presigned URL is an
 * expected shape of failure here (an expired link, a capture that was cleaned up), not a programming
 * error, so it resolves like every other outcome instead of unwinding the caller's `then` chain.
 *
 * **The fallback re-creates the exact console signature verifime-portal-web#2047 reported.** Once the
 * raw URL reaches the component, the browser blocks it under `img-src` and logs a CSP violation
 * identical to the original bug's. Left on its own, that would make a transient fetch failure
 * (an expired link, a network blip) indistinguishable from this fix having regressed. The
 * `console.warn` below fires first, so the console shows *why* the raw URL was handed back before
 * the browser's own CSP message appears: its presence marks the known fallback path, its absence
 * ahead of a CSP violation is what a genuine regression would look like instead.
 */
export async function toDisplayableCapture(url?: string): Promise<TDisplayableCapture> {
  if (!url) {
    return { src: undefined, release: NOTHING_CREATED };
  }

  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.warn(
        '[toDisplayableCapture] falling back to the raw capture URL: fetch responded with',
        response.status,
      );
      return { src: url, release: NOTHING_CREATED };
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    return { src: objectUrl, release: () => URL.revokeObjectURL(objectUrl) };
  } catch (error) {
    console.warn('[toDisplayableCapture] falling back to the raw capture URL: fetch failed', error);
    return { src: url, release: NOTHING_CREATED };
  }
}
